/**
 * Formatting for the Backups pages.
 *
 * Past times reuse `relativeTime` from monitoring, so "3 hours ago" reads the same everywhere. What
 * lives here is what that helper cannot do: a *future* time ("in 19 hours"), which it clamps to
 * "just now" — how a schedule's next run used to read "next just now" for a run a day away.
 */

export function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1073741824) return `${(bytes / 1048576).toFixed(1)} MB`;

  return `${(bytes / 1073741824).toFixed(2)} GB`;
}

/** When something is due: "in 19 hours", or "due now" once that moment has come. */
export function timeUntil(iso: string): string {
  const seconds = Math.round((new Date(iso).getTime() - Date.now()) / 1000);

  if (seconds < 60) {
    return 'due now';
  }

  if (seconds < 3600) {
    const minutes = Math.round(seconds / 60);

    return `in ${minutes} minute${minutes === 1 ? '' : 's'}`;
  }

  if (seconds < 86400) {
    const hours = Math.round(seconds / 3600);

    return `in ${hours} hour${hours === 1 ? '' : 's'}`;
  }

  const days = Math.round(seconds / 86400);

  return `in ${days} day${days === 1 ? '' : 's'}`;
}

/** The compact form an activity row can afford: "just now", "12m ago", "3h ago", "2d ago". */
export function compactAgo(iso: string): string {
  const seconds = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h ago`;

  return `${Math.round(seconds / 86400)}d ago`;
}

/** A short date and time for a table cell: "Sep 13, 09:14". */
export function shortDateTime(iso: string): string {
  const date = new Date(iso);

  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...(date.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }),
  });
}

/** The exact local date and time, for the tooltip behind a relative one. */
export function exactTime(iso: string): string {
  return new Date(iso).toLocaleString();
}
